import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import type { ProductCardWidgetData } from '@web/widgets/product-card/type';
import { CardKindViewType, type GeneralSettings, RecurringMode } from '@web/widgets/product-card/type';
import type { Classes } from 'jss';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { EButtonStyleType } from '@common/enums';
import Swiper, { Autoplay, EffectCards, EffectFade, FreeMode, Navigation, Pagination, Thumbs } from 'swiper';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { SpSafeHtmlPipe } from '@common/pipes/safe-html.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { GalleryViewsComponent } from './gallery-views/gallery-views.component';
import { CommonModule } from '@angular/common';
import { type Observable } from 'rxjs';
import { LayoutFacade } from '@web/store/layout/layout.facade';

Swiper.use([Autoplay, Pagination, Thumbs, Navigation, EffectCards, EffectFade, FreeMode]);

@Component({
    selector: 'web-open-view-card',
    styleUrls: ['./open-view-card.component.less'],
    templateUrl: './open-view-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, GalleryViewsComponent, FormsModule, TranslateModule, SpSafeHtmlPipe],
})
export class OpenViewCardComponent {
    protected readonly settingsFacade = inject(SettingsFacade);
    protected readonly layoutFacade = inject(LayoutFacade);

    @Input() data: ProductCardWidgetData;
    @Input() classes: Classes;
    @Input() widgetId: string;

    public readonly shopSettings$ = this.settingsFacade.shopSettings$;
    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;

    public get element(): ProductCardWidgetData['element'] {
        return this.data.element;
    }

    public get basketButton(): ProductCardWidgetData['basketButton'] {
        return this.data.basketButton;
    }

    public get getBasketButtonStyles(): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[this.data.basketButton.size]}`;
        const shadowClass = this.classes?.basketButtonShadow ?? '';

        if (this.basketButton.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.buttonStyles} ${shadowClass}`;
        } else {
            return `${styles} ${BUTTON_TYPE_CLASS_MAP[this.basketButton.styleType]} ${shadowClass}`;
        }
    }

    public get generalSettings(): GeneralSettings {
        return this.data.cardSettings.general as GeneralSettings;
    }

    public get isFullPageImage(): boolean {
        return this.data.cardKindViewType === CardKindViewType.eighth;
    }

    public get backgroundUrl(): string {
        return `url(//${this.element?.images[0].defaultCdnUrl}${this.element.images[0]?.url})`;
    }

    public get isShowCatalogCard(): boolean {
        if (!this.element?.catalogProductId) return true;

        const isVisible = this.element?.visible ?? 1;
        return !!this.element?.catalogProductId && !!isVisible;
    }

    public get subscriptionPeriodKey(): string | null {
        if (!this.isSubscriptionType) {
            return;
        }

        switch (this.element?.subscriptionPeriod) {
            case RecurringMode.daily:
                return 'widget_product_subscription_period_day';
            case RecurringMode.weekly:
                return 'widget_product_subscription_period_week';
            case RecurringMode.monthly:
                return 'widget_product_subscription_period_month';
            case RecurringMode.quarterly:
                return 'widget_product_subscription_period_3_months';
            case RecurringMode.halfyearly:
                return 'widget_product_subscription_period_6_months';
            case RecurringMode.yearly:
                return 'widget_product_subscription_period_year';
            default:
                return null;
        }
    }

    public get isSubscriptionType(): boolean {
        return Boolean(this.element?.subscriptionPeriod);
    }

    protected readonly themeColorCss = themeColorCss;
}
